import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiMail, FiKey, FiCreditCard, FiAlertTriangle, FiCheck, FiEdit2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import PasskeyManager from '../components/Passkey/PasskeyManager';

const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isGuest, logout, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [subscription, setSubscription] = useState<any>(null);
  
  // Form states
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // Redirect guests to register
    if (isGuest) {
      navigate('/register');
      toast('Please create an account to access settings', { icon: '👤' });
      return;
    }

    // Initialize form values
    if (user) {
      setDisplayName(user.displayName || user.username || '');
    }

    // Load subscription status
    loadSubscription();
  }, [isGuest, navigate, user]);

  const loadSubscription = async () => {
    try {
      const response = await api.getSubscriptionStatus();
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    }
  };

  const handleUpdateProfile = async () => {
    setIsLoading(true);
    try {
      // This endpoint would need to be implemented in the backend
      await api.post('/auth/update-profile', {
        displayName
      });
      
      await refreshUser();
      setEditMode(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);
    try {
      // This endpoint would need to be implemented in the backend
      await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading(true);
    try {
      const response = await api.createBillingPortalSession(
        `${window.location.origin}/account`
      );
      window.location.href = response.data.portalUrl;
    } catch (error) {
      toast.error('Failed to open billing portal');
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently deleted.'
    );

    if (!confirmed) return;

    const doubleConfirm = window.prompt(
      'Please type "DELETE" to confirm account deletion:'
    );

    if (doubleConfirm !== 'DELETE') {
      toast.error('Account deletion cancelled');
      return;
    }

    setIsLoading(true);
    try {
      // This endpoint would need to be implemented in the backend
      await api.post('/auth/delete-account');
      await logout();
      navigate('/');
      toast.success('Account deleted successfully');
    } catch (error) {
      toast.error('Failed to delete account');
      setIsLoading(false);
    }
  };

  if (isGuest || !user) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>

        {/* Profile Section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <FiUser className="w-5 h-5 mr-2" />
                Profile Information
              </h2>
              {!editMode && (
                <button
                  onClick={() => setEditMode(true)}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm flex items-center"
                >
                  <FiEdit2 className="w-4 h-4 mr-1" />
                  Edit
                </button>
              )}
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="flex items-center">
                  <FiMail className="w-5 h-5 text-gray-400 mr-2" />
                  <span className="text-gray-900">{user.email}</span>
                  {user.emailVerified && (
                    <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <FiCheck className="w-3 h-3 mr-1" />
                      Verified
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                {editMode ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter display name"
                    />
                    <button
                      onClick={handleUpdateProfile}
                      disabled={isLoading}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setDisplayName(user.displayName || user.username || '');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-900">
                    {user.displayName || user.username || 'Not set'}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type
                </label>
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    user.tier === 'pro' 
                      ? 'bg-primary-100 text-primary-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.tier === 'pro' ? 'Pro' : 'Free'} Account
                  </span>
                  {user.tier !== 'pro' && (
                    <button
                      onClick={() => navigate('/upgrade')}
                      className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                    >
                      Upgrade to Pro
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Passkey Section */}
        <div className="mb-6">
          <PasskeyManager />
        </div>

        {/* Password Section */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <FiKey className="w-5 h-5 mr-2" />
              Password & Security
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter new password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Confirm new password"
                />
              </div>

              <button
                onClick={handleChangePassword}
                disabled={isLoading || !currentPassword || !newPassword}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Change Password
              </button>
            </div>

          </div>
        </div>

        {/* Billing Section */}
        {subscription && (
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <FiCreditCard className="w-5 h-5 mr-2" />
                Billing & Subscription
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Plan
                  </label>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 font-medium capitalize">
                      {subscription.tier} Plan
                    </span>
                    {subscription.tier === 'pro' ? (
                      <button
                        onClick={handleManageBilling}
                        disabled={isLoading}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        Manage Billing
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/upgrade')}
                        className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        Upgrade to Pro
                      </button>
                    )}
                  </div>
                </div>

                {subscription.endsAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {subscription.status === 'canceling' ? 'Cancels On' : 'Renews On'}
                    </label>
                    <span className="text-gray-900">
                      {new Date(subscription.endsAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200">
          <div className="p-6 border-b border-red-200 bg-red-50">
            <h2 className="text-xl font-semibold text-red-900 flex items-center">
              <FiAlertTriangle className="w-5 h-5 mr-2" />
              Danger Zone
            </h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900 mb-2">Delete Account</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Once you delete your account, there is no going back. All your data will be permanently deleted.
                </p>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountPage;